/* @flow */

import React, { PureComponent, Fragment, createElement } from 'react'
import type { ComponentType, Element } from 'react'

export type ComponentsType = { [string]: { component: ComponentType<any> | string, props: Object } }
type PropsType = { text: ?string, components: ComponentsType }
type StateType = {}
type ElementType = { props: Object, close: boolean, openClose: boolean, open: boolean, name: string }
type ElementNodeType = { ...ElementType, children: ChildrenType, parent: RootNodeType | ElementNodeType }
type TextNodeType = { text: string, children: [] }
type RootNodeType = { children: ChildrenType }
type NodeType = ElementNodeType | TextNodeType
type ChildrenType = Array<NodeType>

const regexNodes = /(<([^>]+)>)/gi
const regexProps = /(\S+)=["']?((?:.(?!["']?\s+(?:\S+)=|[>"']))+.)["']?/g
const regexOpenClose = /^<.*\/([ ]*)>$/
const regexClose = /^<\//
const regexName = /^<\/?([\w]+)/

// https://www.w3schools.com/charsets/ref_utf_basic_latin.asp
const htmlEntities = {
  nbsp: '\u00A0',
  amp: '\u0026',
  lt: '\u003C',
  gt: '\u003E',
  euro: '\u20AC'
}

const entityToUTF8 = (entity: string) => {
  const hex = /^&#x/.test(entity)
  const match = entity.match(/&#x?([a-z0-9]+);/i)
  if (!match || match.length < 2) return entity
  let str = match[1]
  let num = hex ? parseInt(str, 16) : parseInt(str, 10)
  return String.fromCharCode(num)
}

const entityToSymbol = (entity: string) => {
  const match = entity.match(/&([a-z]+);/i)
  if (!match || match.length < 2) return entity
  let str = match[1]
  return htmlEntities[str]
}

const htmlEntitiesReplace = (text: string) => {
  const entities = text.match(/&#([a-z0-9]+);/gi)
  if (entities) {
    entities.forEach((entity) => {
      text = text.replace(entity, entityToUTF8(entity))
    })
  }
  const entities2 = text.match(/&([a-z]+);/gi)
  if (entities2) {
    entities2.forEach((entity) => {
      text = text.replace(entity, entityToSymbol(entity))
    })
  }

  return text
}

const elementProps = (element: string) => {
  return (element.match(regexProps) || []).reduce((obj, entry) => {
    const [key, value] = entry.split('=', 2)
    obj[key] = value.slice(1, -1)
    return obj
  }, {})
}

const elementName = (element: string) => {
  const match = element.match(regexName)
  if (!match || match.length < 2) return ''
  return match[1]
}

const analyseElement = (element: string): ElementType => {
  const close = regexClose.test(element)
  const openClose = regexOpenClose.test(element)
  return {
    props: elementProps(element),
    close: close,
    openClose: openClose,
    open: !close && !openClose,
    name: elementName(element)
  }
}

const createNode = (element: ElementType, parent: RootNodeType | ElementNodeType): ElementNodeType => {
  return { ...element, children: [], parent }
}

const parser = (text: string): RootNodeType => {
  text = htmlEntitiesReplace(text)
  const match = text.match(regexNodes)
  if (!match) return { children: [{ text: text, children: [] }] }
  const root: RootNodeType = { children: [] }
  let active: RootNodeType | ElementNodeType = root
  match.forEach((entry, index) => {
    const parts = (text || '').split(entry)
    const textBefore = parts.shift()
    const textAfter = parts.join(entry)
    if (textBefore && textBefore.length > 0) active.children.push({ text: textBefore, children: [] })
    const element = analyseElement(entry)
    if (element.openClose) {
      active.children.push(createNode(element, active))
    } else if (element.open) {
      const node = createNode(element, active)
      active.children.push(node)
      active = node
    } else if (element.close) {
      // $FlowFixMe active.parent not know
      active = active.parent
    }
    if (match.length == index + 1) {
      if (textAfter && textAfter.length > 0) active.children.push({ text: textAfter, children: [] })
    } else {
      text = textAfter
    }
  })
  return root
}

const renderElements = (nodes: ChildrenType, components: ComponentsType = {}): Array<Element<*> | null> => {
  return nodes.map((node: NodeType, index) => {
    if (typeof node.name == 'string') {
      // $FlowFixMe node.children not know
      const children = node.openClose ? null : renderElements(node.children, components)
      if (components[node.name]) {
        const component = components[node.name]
        return createElement(
          component.component,
          Object.assign({ key: index }, node.props || {}, component.props || {}),
          children
        )
      }
      return createElement(node.name, Object.assign({ key: index }, node.props || {}), children)
    } else if (typeof node.text == 'string') {
      return <Fragment key={index}>{node.text}</Fragment>
    }
    return null
  })
}

class TranslationWithComponents extends PureComponent<PropsType, StateType> {
  render() {
    const { text, components } = this.props
    const rootNode = parser(text || '')
    return renderElements(rootNode.children, components)
  }
}

export default TranslationWithComponents
